import { SafetyCertificateOutlined } from "@ant-design/icons";
import { AppPaths } from "@p0security/shared/routes/constants";
import {
  Avatar,
  Badge,
  Button,
  Descriptions,
  Drawer,
  Flex,
  Input,
  List,
  Progress,
  Tabs,
  Typography,
} from "antd";
import { useCallback, useState } from "react";
import { colors } from "styles/variables";

import { useNavigateWithEnv } from "../../../Assessment/hooks/useNavigateWithEnv";
import {
  GovernancePolicy,
  GovernanceViolator,
  getGovernancePolicyStats,
} from "./governancePolicies";

type Props = {
  policy: GovernancePolicy | undefined;
  onClose: () => void;
};

const ViolatorRow: React.FC<{ violator: GovernanceViolator }> = ({
  violator,
}) => (
  <List.Item>
    <List.Item.Meta
      avatar={<Avatar shape="square" icon={<SafetyCertificateOutlined />} />}
      title={violator.name}
      description={`${violator.platform} / ${violator.source} / discovered ${violator.discoveredAt}`}
    />
  </List.Item>
);

const MCP_SERVER_POLICY_ID = "mcp-server-must-be-managed";

export const GovernanceDetailDrawer: React.FC<Props> = ({
  policy,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState("needs-attention");
  const navigateWithEnv = useNavigateWithEnv();

  const handleTabChange = useCallback(
    (key: string) => setActiveTab(key),
    [setActiveTab]
  );

  const hasViolators = (policy?.violators.length ?? 0) > 0;
  const filterUnmanaged = policy?.id === MCP_SERVER_POLICY_ID && hasViolators;

  const handleViewAllViolators = useCallback(() => {
    navigateWithEnv(
      filterUnmanaged
        ? { pathname: AppPaths.AI, search: "?status=unmanaged" }
        : AppPaths.AI,
      undefined,
      true
    );
    onClose();
  }, [navigateWithEnv, onClose, filterUnmanaged]);

  if (!policy) return null;

  const { compliancePercent, ratio, totalCount } =
    getGovernancePolicyStats(policy);
  const tone =
    compliancePercent >= 90
      ? colors.success["50"]
      : compliancePercent >= 60
        ? colors.themeColors.lightOrange
        : colors.themeColors.red;

  return (
    <Drawer
      open={!!policy}
      onClose={onClose}
      width={640}
      destroyOnClose
      title={
        <Flex gap={12} align="center">
          <Avatar
            shape="square"
            icon={<SafetyCertificateOutlined />}
            style={{
              background: colors.tagColors.orange.bg,
              color: colors.tagColors.orange.text,
            }}
          />
          <Flex vertical>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {policy.name}
            </Typography.Title>
            <Typography.Text type="secondary">
              Posture rule / {policy.appliesToLabel}
            </Typography.Text>
          </Flex>
        </Flex>
      }
      footer={
        <Flex justify="flex-end">
          <Button type="primary" onClick={handleViewAllViolators}>
            View all violators
          </Button>
        </Flex>
      }
    >
      <Typography.Paragraph type="secondary">
        {policy.description}
      </Typography.Paragraph>
      <Descriptions column={3} size="small">
        <Descriptions.Item label="Status">
          <Badge status="success" text="Enabled" />
        </Descriptions.Item>
        <Descriptions.Item label="Compliance">
          <Flex vertical gap={4}>
            <Typography.Text code style={{ color: tone }}>
              {ratio} ({compliancePercent}%)
            </Typography.Text>
            <Progress
              percent={compliancePercent}
              showInfo={false}
              strokeColor={tone}
              size="small"
            />
          </Flex>
        </Descriptions.Item>
        <Descriptions.Item label="Applies to">
          {policy.appliesToLabel}
        </Descriptions.Item>
      </Descriptions>
      <Flex vertical gap={8} style={{ marginTop: 16 }}>
        <Flex gap={8} align="center">
          <Typography.Text strong>Query</Typography.Text>
          <Typography.Text type="secondary" code>
            {policy.query.show}
          </Typography.Text>
        </Flex>
        <Input.TextArea
          autoSize={{ minRows: 2, maxRows: 4 }}
          readOnly
          value={policy.query.where}
        />
      </Flex>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: "needs-attention",
            label: `Needs attention (${policy.violators.length})`,
            children:
              policy.violators.length > 0 ? (
                <List
                  dataSource={policy.violators}
                  // eslint-disable-next-line react/jsx-no-bind
                  renderItem={(v) => <ViolatorRow key={v.id} violator={v} />}
                />
              ) : (
                <Typography.Text type="secondary">
                  No entities need attention.
                </Typography.Text>
              ),
          },
          {
            key: "enforced",
            label: `Enforced (${ratio})`,
            children: (
              <Typography.Text type="secondary">
                {policy.enforcedCount} of {totalCount} entities currently meet
                this policy.
              </Typography.Text>
            ),
          },
        ]}
      />
    </Drawer>
  );
};
